'use client'

import React, { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DollarSign, Download, Calendar, TrendingUp, FileText, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { usePayroll } from '@/hooks/queries'

interface PayrollRecord {
  id: string
  payPeriod: string
  grossPay: number
  netPay: number
  totalDeductions: number
  totalTax: number
  totalBonuses: number
  paymentStatus: string
  paidDate?: string
}

interface PayrollSummaryProps {
  staffId: string
  clinicId: string
  year?: number
}

export default function PayrollSummary({
  staffId,
  clinicId,
  year = new Date().getFullYear()
}: PayrollSummaryProps) {
  const { t } = useTranslation('hr')
  // Use React Query for caching - 30 minute cache
  const { 
    data: queryData, 
    isLoading: loading, 
    refetch,
    isFetching 
  } = usePayroll(clinicId, `${year}`)

  const records = queryData?.payrollRecords || []
  const ytdTotals = queryData?.yearToDateTotals || {
    grossPay: 0,
    netPay: 0,
    totalDeductions: 0,
    totalTax: 0,
    totalBonuses: 0
  }
  const salaryInfo = queryData?.salaryInfo || null

  // Legacy function for compatibility
  const fetchPayrollData = () => {
    refetch()
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <Badge variant="default" className="bg-green-600">{t('payroll.paid')}</Badge>
      case 'pending':
        return <Badge variant="outline">{t('leave.pending')}</Badge>
      case 'processing':
        return <Badge variant="secondary">{t('payroll.processing')}</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  const downloadPayslip = (recordId: string) => {
    window.open(`/api/hr/payroll/payslip?id=${recordId}`, '_blank')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Current Salary Info */}
      {salaryInfo && (
        <Card>
          <CardHeader>
            <CardTitle>{t('payroll.currentSalary')}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-muted-foreground">{t('payroll.baseSalary')}</div>
              <div className="text-2xl font-bold">{formatCurrency(salaryInfo.baseSalary)}</div>
              <div className="text-xs text-muted-foreground">{t('payroll.perMonth')}</div>
            </div>
            {salaryInfo.allowances > 0 && (
              <div>
                <div className="text-sm text-muted-foreground">{t('payroll.allowances')}</div>
                <div className="text-xl font-semibold">{formatCurrency(salaryInfo.allowances)}</div>
              </div>
            )}
            <div>
              <div className="text-sm text-muted-foreground">{t('payroll.paymentSchedule')}</div>
              <div className="font-medium">{salaryInfo.paymentSchedule || t('payroll.monthly')}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">{t('payroll.effectiveSince')}</div>
              <div className="font-medium">
                {new Date(salaryInfo.effectiveDate).toLocaleDateString('sv-SE')}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Year-to-Date Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t('payroll.ytdSummary', { year })}</span>
            <Badge variant="outline">{t('payroll.payments', { count: records.length })}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">{t('payroll.grossPay')}</div>
              <div className="text-2xl font-bold">{formatCurrency(ytdTotals.grossPay)}</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">{t('payroll.deductions')}</div>
              <div className="text-2xl font-bold text-red-600">
                -{formatCurrency(ytdTotals.totalDeductions)}
              </div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">{t('payroll.tax')}</div>
              <div className="text-2xl font-bold text-red-600">
                -{formatCurrency(ytdTotals.totalTax)}
              </div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">{t('payroll.bonuses')}</div>
              <div className="text-2xl font-bold text-green-600">
                +{formatCurrency(ytdTotals.totalBonuses)}
              </div>
            </div>
            <div className="p-4 border rounded-lg bg-primary/5">
              <div className="text-sm text-muted-foreground">{t('payroll.netPay')}</div>
              <div className="text-2xl font-bold text-primary">
                {formatCurrency(ytdTotals.netPay)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Payroll Records */}
      <Card>
        <CardHeader>
          <CardTitle>{t('payroll.payrollHistory')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {records.length === 0 ? (
              <div className="text-center py-8">
                <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">{t('payroll.noRecords', { year })}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {t('payroll.recordsHint')}
                </p>
              </div>
            ) : (
              records.map((record) => (
                <div
                  key={record.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <Calendar className="h-5 w-5 text-muted-foreground mx-auto" />
                      <div className="text-sm font-medium mt-1">
                        {record.payPeriod}
                      </div>
                    </div>
                    <div className="border-l pl-4">
                      <div className="flex items-center gap-4">
                        <div>
                          <div className="text-xs text-muted-foreground">{t('payroll.gross')}</div>
                          <div className="font-medium">{formatCurrency(record.grossPay)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">{t('payroll.deductions')}</div>
                          <div className="font-medium text-red-600">
                            -{formatCurrency(record.totalDeductions + record.totalTax)}
                          </div>
                        </div>
                        {record.totalBonuses > 0 && (
                          <div>
                            <div className="text-xs text-muted-foreground">{t('payroll.bonuses')}</div>
                            <div className="font-medium text-green-600">
                              +{formatCurrency(record.totalBonuses)}
                            </div>
                          </div>
                        )}
                        <div>
                          <div className="text-xs text-muted-foreground">{t('payroll.netPay')}</div>
                          <div className="text-lg font-bold text-primary">
                            {formatCurrency(record.netPay)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {getStatusBadge(record.paymentStatus)}
                    {record.paidDate && (
                        <div className="text-xs text-muted-foreground">
                          {t('payroll.paid')}: {new Date(record.paidDate).toLocaleDateString('sv-SE')}
                        </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadPayslip(record.id)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {t('payroll.payslip')}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tax Summary */}
      <Card>
        <CardHeader>
          <CardTitle>{t('payroll.taxSummary', { year })}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">{t('payroll.totalIncome')}</div>
              <div className="text-xl font-bold">{formatCurrency(ytdTotals.grossPay)}</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">{t('payroll.totalTaxPaid')}</div>
              <div className="text-xl font-bold">{formatCurrency(ytdTotals.totalTax)}</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm text-muted-foreground">{t('payroll.averageTaxRate')}</div>
              <div className="text-xl font-bold">
                {ytdTotals.grossPay > 0
                  ? ((ytdTotals.totalTax / ytdTotals.grossPay) * 100).toFixed(1)
                  : 0}%
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

